import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Shield, Lock, Database, Server, FileCheck, AlertTriangle, MessageSquare, HelpCircle } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';

const texts = {
  fr: {
    titre: 'Paiements, Sécurité & Architecture',
    structure: 'Structure des Paiements',
    regulier: 'Régulier',
    parTransaction: 'Par transaction',
    sezzle: 'Sezzle BNPL',
    volume: '+18% à +25% volume',
    abos: 'Abonnements',
    auxEcoles: '50% écoles (don)',
    psp: 'PSP : SonaPay',
    securite: 'Sécurité',
    infrastructure: 'Infrastructure',
    items: {
      multiTenant: 'Multi-tenant isolé (RBAC)',
      chiffrement: 'Chiffrement end-to-end',
      waf: 'WAF/DDoS protection',
      sauvegardes: 'Sauvegardes automatiques',
      audit: 'Audit logs complets',
      conformite: 'Conformité LPRPDE/PIPEDA & Loi 25',
      messagerie: 'Messagerie RBAC chiffrée',
      donneesQC: 'Données hébergées au Québec',
      serveursQC: 'Serveurs au Québec',
      conformiteLois: 'Conformité LPRPDE/PIPEDA et loi 25',
      certifSOC2: 'Certifications SOC 2 (objectif Y2)'
    },
    tooltips: {
      rbac: 'Role-Based Access Control. Système de permissions hiérarchique où chaque utilisateur (parent, école, traiteur, CSS) n\'accède qu\'aux données pertinentes selon son rôle, garantissant isolation et sécurité.',
      encryption: 'Chiffrement de bout en bout. Les données sont cryptées durant tout leur parcours, du départ à l\'arrivée, empêchant tout accès non autorisé même en cas d\'interception.',
      waf: 'Web Application Firewall et protection contre les attaques par déni de service distribué. Bouclier avancé contre les cyberattaques, intrusions et surcharges malveillantes.',
      lprpde: 'Loi sur la protection des renseignements personnels et les documents électroniques (LPRPDE/PIPEDA). Loi fédérale canadienne établissant les règles pour la collecte, l\'utilisation et la divulgation de renseignements personnels dans le secteur privé.',
      gdpr: 'Loi sur la protection des renseignements personnels et les documents électroniques (LPRPDE/PIPEDA). Loi fédérale canadienne établissant les règles pour la collecte, l\'utilisation et la divulgation de renseignements personnels dans le secteur privé.',
      loi25: 'Loi 25 : Modernisation législative en matière de protection des renseignements personnels. Loi québécoise stricte renforçant la protection des données et les droits des citoyens, alignée avec les standards internationaux.',
      soc2: 'Service Organization Control 2. Certification internationale attestant des contrôles rigoureux en matière de sécurité, disponibilité, intégrité, confidentialité et vie privée des données.',
      sezzle: 'Buy Now, Pay Later. Solution de paiement fractionné permettant aux parents d\'étaler le coût des repas sur plusieurs versements sans intérêt, augmentant l\'accessibilité et le volume de commandes.'
    }
  },
  en: {
    titre: 'Payments, Security & Architecture',
    structure: 'Payment Structure',
    regulier: 'Regular',
    parTransaction: 'Per transaction',
    sezzle: 'Sezzle BNPL',
    volume: '+18% to +25% volume',
    abos: 'Subscriptions',
    auxEcoles: '50% schools (donation)',
    psp: 'PSP: SonaPay',
    securite: 'Security',
    infrastructure: 'Infrastructure',
    items: {
      multiTenant: 'Multi-tenant isolated (RBAC)',
      chiffrement: 'End-to-end encryption',
      waf: 'WAF/DDoS protection',
      sauvegardes: 'Automated backups',
      audit: 'Complete audit logs',
      conformite: 'PIPEDA & Law 25 compliance',
      messagerie: 'RBAC encrypted messaging',
      donneesQC: 'Data hosted in Quebec',
      serveursQC: 'Servers in Quebec',
      conformiteLois: 'PIPEDA and Law 25 compliance',
      certifSOC2: 'SOC 2 certifications (Y2 target)'
    },
    tooltips: {
      rbac: 'Role-Based Access Control. Hierarchical permission system where each user (parent, school, caterer, board) only accesses relevant data based on their role, ensuring isolation and security.',
      encryption: 'End-to-end encryption. Data is encrypted throughout its entire journey, from departure to arrival, preventing any unauthorized access even if intercepted.',
      waf: 'Web Application Firewall and Distributed Denial of Service protection. Advanced shield against cyberattacks, intrusions, and malicious overloads.',
      pipeda: 'Personal Information Protection and Electronic Documents Act (PIPEDA). Canadian federal law establishing rules for collection, use, and disclosure of personal information in the private sector.',
      gdpr: 'Personal Information Protection and Electronic Documents Act (PIPEDA). Canadian federal law establishing rules for collection, use, and disclosure of personal information in the private sector.',
      loi25: 'Law 25: Legislative modernization for personal information protection. Strict Quebec law strengthening data protection and citizen rights, aligned with international standards.',
      soc2: 'Service Organization Control 2. International certification attesting rigorous controls for security, availability, integrity, confidentiality, and data privacy.',
      sezzle: 'Buy Now, Pay Later. Split payment solution allowing parents to spread meal costs over multiple interest-free installments, increasing accessibility and order volume.'
    }
  }
};

export default function PaiementsPage() {
  const { language } = useLanguage();
  const t = texts[language];
  
  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <PartieBadge path="/paiements" />
        <Card className="bg-black/60 border-cyan-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">{t.titre}</CardTitle>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-lime-500 to-cyan-500 rounded-full" />
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Structure paiements - Tiered */}
            <div>
              <h3 className="text-xl font-bold text-lime-400 mb-4">{t.structure}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-black/40 border-lime-500/30">
                  <CardContent className="p-4">
                    <p className="text-[10px] text-lime-400 uppercase tracking-wider mb-1">{language === 'fr' ? 'Tier 1 (1-25 traiteurs)' : 'Tier 1 (1-25 caterers)'}</p>
                    <p className="text-sm text-gray-400 mb-2">{t.regulier}</p>
                    <p className="text-xl font-bold text-lime-400">2,9% + 0,30$</p>
                    <p className="text-xs text-lime-300 mt-1">{language === 'fr' ? 'Marge nette' : 'Net margin'} : 1,2% + 0,12$</p>
                    <p className="text-xs text-gray-500 mt-2">SaaS : 0$</p>
                  </CardContent>
                </Card>
                <Card className="bg-black/40 border-orange-500/30">
                  <CardContent className="p-4">
                    <p className="text-[10px] text-orange-400 uppercase tracking-wider mb-1">{language === 'fr' ? 'Tier 2 (26+ traiteurs)' : 'Tier 2 (26+ caterers)'}</p>
                    <p className="text-sm text-gray-400 mb-2">{t.regulier}</p>
                    <p className="text-xl font-bold text-orange-400">3,9% + 0,30$</p>
                    <p className="text-xs text-orange-300 mt-1">{language === 'fr' ? 'Marge nette' : 'Net margin'} : 2,2% + 0,12$</p>
                    <p className="text-xs text-gray-500 mt-2">SaaS : min 150$/mois</p>
                  </CardContent>
                </Card>
                <Card className="bg-black/40 border-cyan-500/30">
                  <CardContent className="p-4">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="text-sm text-gray-400 mb-2 cursor-pointer border-b border-dotted border-cyan-500/50 inline-flex items-center gap-1 hover:text-cyan-300 transition-colors" data-testid="sezzle-tooltip-btn">
                          {t.sezzle}
                          <HelpCircle className="h-3 w-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="bg-black/95 border-cyan-500/50 text-white max-w-xs">
                        <p className="text-xs">{t.tooltips.sezzle}</p>
                      </PopoverContent>
                    </Popover>
                    <p className="text-2xl font-bold text-cyan-400">2%</p>
                    <p className="text-xs text-gray-500 mt-2">{t.volume}</p>
                    <p className="text-[10px] text-lime-400 mt-1">✓ {language === 'fr' ? 'Déjà activé' : 'Already activated'}</p>
                  </CardContent>
                </Card>
                <Card className="bg-black/40 border-purple-500/30">
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-400 mb-2">{t.abos}</p>
                    <p className="text-2xl font-bold text-purple-400">2$/mois</p>
                    <p className="text-xs text-gray-500 mt-2">{t.auxEcoles}</p>
                  </CardContent>
                </Card>
              </div>
              {/* EcoLunch Connect */}
              <div className="mt-4 p-4 bg-gradient-to-r from-lime-500/10 via-cyan-500/10 to-transparent rounded-lg border border-lime-500/30" data-testid="ecolunch-connect-card">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-sm font-bold text-lime-400 uppercase tracking-wider">EcoLunch Connect</p>
                    <p className="text-xs text-gray-300 mt-1">{language === 'fr' ? 'Couche de revenus additionnelle — par référence d\'école au traiteur' : 'Additional revenue layer — per school referral to caterer'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-cyan-400">1% à 2,5%</p>
                    <p className="text-[10px] text-gray-400">{language === 'fr' ? 'par référence (selon intégration)' : 'per referral (per integration)'}</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 bg-lime-500/10 rounded-lg border border-lime-500/30">
                <p className="text-sm text-lime-400 font-semibold">{t.psp}</p>
              </div>
            </div>

            {/* Sécurité */}
            <div>
              <h3 className="text-xl font-bold text-cyan-400 mb-4">{t.securite}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { icon: Shield, label: t.items.multiTenant, tooltip: t.tooltips.rbac },
                  { icon: Lock, label: t.items.chiffrement, tooltip: t.tooltips.encryption },
                  { icon: AlertTriangle, label: t.items.waf, tooltip: t.tooltips.waf },
                  { icon: Database, label: t.items.sauvegardes },
                  { icon: FileCheck, label: t.items.audit },
                  { icon: Server, label: t.items.conformite, tooltip: `${t.tooltips.lprpde || t.tooltips.pipeda}\n\n${t.tooltips.loi25}` },
                  { icon: MessageSquare, label: t.items.messagerie, tooltip: t.tooltips.rbac }
                ].map((item, idx) => {
                  const Icon = item.icon;
                  const content = (
                    <div className="flex items-start gap-3 p-3 bg-black/40 rounded-lg border border-cyan-500/20">
                      <Icon className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-300">{item.label}</span>
                      {item.tooltip && <HelpCircle className="h-3 w-3 text-cyan-400/60 flex-shrink-0 mt-0.5" />}
                    </div>
                  );
                  
                  if (item.tooltip) {
                    return (
                      <Popover key={idx}>
                        <PopoverTrigger asChild>
                          <button type="button" className="cursor-pointer text-left w-full hover:bg-cyan-500/5 rounded-lg transition-colors" data-testid={`security-tooltip-${idx}`}>
                            {content}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="bg-black/95 border-cyan-500/50 text-white max-w-sm">
                          <p className="text-xs whitespace-pre-line">{item.tooltip}</p>
                        </PopoverContent>
                      </Popover>
                    );
                  }
                  return <div key={idx}>{content}</div>;
                })}
              </div>
            </div>

            {/* Infrastructure */}
            <div className="p-6 bg-gradient-to-br from-orange-500/10 to-transparent rounded-xl border border-orange-500/30">
              <h3 className="text-xl font-bold text-orange-400 mb-4">{t.infrastructure}</h3>
              <div className="space-y-3">
                <p className="text-gray-300 flex items-start gap-2">
                  <span className="text-lime-500 mt-1">✓</span>
                  <span>{t.items.donneesQC}</span>
                </p>
                <p className="text-gray-300 flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">✓</span>
                  <span>{t.items.serveursQC}</span>
                </p>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-gray-300 flex items-start gap-2 cursor-pointer text-left hover:bg-orange-500/5 rounded transition-colors w-full" data-testid="infra-conformite-btn">
                      <span className="text-orange-500 mt-1">✓</span>
                      <span className="border-b border-dotted border-orange-500/50">{t.items.conformiteLois}</span>
                      <HelpCircle className="h-3 w-3 text-orange-400/60 mt-1" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="bg-black/95 border-orange-500/50 text-white max-w-sm">
                    <p className="text-xs whitespace-pre-line">{`${t.tooltips.gdpr}\n\n${t.tooltips.loi25}`}</p>
                  </PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-gray-300 flex items-start gap-2 cursor-pointer text-left hover:bg-lime-500/5 rounded transition-colors w-full" data-testid="infra-soc2-btn">
                      <span className="text-lime-500 mt-1">✓</span>
                      <span className="border-b border-dotted border-lime-500/50">{t.items.certifSOC2}</span>
                      <HelpCircle className="h-3 w-3 text-lime-400/60 mt-1" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="bg-black/95 border-lime-500/50 text-white max-w-sm">
                    <p className="text-xs">{t.tooltips.soc2}</p>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
